<template>
    <div class="aggregate-report mb-3 mt-page" v-if="entityName">

        <h1>{{ $i18n.gt('HEADING_QUESTION_STATISTICS_FOR') }} {{ entityName }}</h1>
        <div class="mb-3">
            <em>{{ $i18n.gt('LABEL_SIMULATED_DATA') }}</em>
        </div>

        <b-row>
            <b-col cols="4"
                   v-for="(qd, i) in filteredQuestionData" :key="i">
                <b-card no-body>
                    <highcharts :options="chartOptions[i]"></highcharts>
                </b-card>
            </b-col>
        </b-row>

    </div>
</template>

<script>
import Vue from 'vue'
import * as _ from 'lodash'
import {Chart} from 'highcharts-vue'
import Highcharts from 'highcharts'
import histogramBellcurve from 'highcharts/modules/histogram-bellcurve'
import GaugeColors from '@/providers/GaugeColors'
import Utils from '@/providers/Utils'

histogramBellcurve(Highcharts)

/**
 * Page to show histograms of aggregate scores for various questions - for a company, plan,
 * practice or practitioner
 */
export default Vue.extend({
    name: 'AggregateReport',
    components: {
        highcharts: Chart
    },
    props: {
        // only one of these should be set
        companyId: Number,
        planId: Number,
        practiceId: Number,
        practitionerId: Number,
    },
    data() {
        return {
            entityName: null,
            questionData: [],
        }
    },
    computed: {
        chartOptions() {
            return _.map(this.filteredQuestionData, (qd, i) => {
                return this.getChartOptions(qd, i)
            })
        },
        filteredQuestionData() {
            return _.filter(this.questionData, (qd) => {
                return qd.scores.length > 0
            })
        }
    },
    methods: {
        numberSuffix: function (d) {
            return Utils.numberSuffix(d)
        },
        getPlotBandSpecs(qd) {
            const specs = []
            const gc = GaugeColors.GAUGE_COLORS
            if (qd.thresholds) {
                for (let i = 0; i <= qd.thresholds.length; i++) {
                    specs.push({
                        from: i == 0 ? 0 : qd.thresholds[i - 1],
                        to: i == qd.thresholds.length ? qd.thresholds[i - 1] * 1.5 : qd.thresholds[i],
                        color: qd.higher_is_better ? gc[qd.thresholds.length - i] : gc[i],
                    })
                }
                specs.push({
                    from: qd.mean,
                    to: qd.mean,
                    color: '#000000',
                    label: {
                        text: 'Mean',
                        style: {
                            color: '#000000',
                        }
                    }
                })
                specs.push({
                    from: qd.median,
                    to: qd.median,
                    color: '#999999',
                    label: {
                        text: 'Median',
                        style: {
                            color: '#999999',
                        },
                        y: 30
                    }
                })
            }
            return specs
        },

        getChartOptions(qd, i) {
            return {
                chart: {
                    height: 200
                },
                title: {
                    text: qd.label
                },
                legend: {enabled: false},
                credits: {enabled: false},

                xAxis: [
                    {
                        title: {text: `Count: ${qd.scores.length}`},
                        plotBands: this.getPlotBandSpecs(qd),
                    },
                ],
                yAxis: [
                    {
                        title: {text: ''},
                        labels: {
                            formatter: function () {
                                // TODO: why is first chart giving NaN%?
                                if (qd.scores.length) {
                                    return Highcharts.numberFormat((this.value / qd.scores.length) * 100, 0) + '%'
                                } else {
                                    return ''
                                }
                            }
                        }
                    }
                ],

                series: [{
                    name: 'Histogram',
                    type: 'histogram',
                    xAxis: 0,
                    yAxis: 0,
                    baseSeries: 's1',
                    binsNumber: 20,
                    pointPadding: 0,
                    zIndex: -1,
                    tooltip: {
                        pointFormatter: function () {
                            const x = Highcharts.numberFormat(this.x, 0, '.', ',')
                            const x2 = Highcharts.numberFormat(this.x2, 0, '.', ',')
                            const y = Highcharts.numberFormat((this.y / qd.scores.length) * 100, 0) + '%'
                            return `${x} - ${x2}: ${y}`
                        }
                    }
                }, {
                    // NB: this series is invisible, but is required to make the histogram work!
                    name: 'Data',
                    type: 'scatter',
                    visible: false,
                    data: qd.scores,
                    id: 's1',
                    marker: {
                        radius: 1.5
                    }
                }]
            }
        }
    },
    mounted() {
        if (this.companyId) {
            this.$api.getAggregateReportDataForCompany(this.companyId)
                .then((data) => {
                    this.entityName = data.entity_name
                    this.questionData = data.stats
                })
        } else if (this.planId) {
            this.$api.getAggregateReportDataForPlan(this.planId)
                .then((data) => {
                    this.entityName = data.entity_name
                    this.questionData = data.stats
                })
        } else if (this.practiceId) {
            this.$api.getAggregateReportDataForPractice(this.practiceId)
                .then((data) => {
                    this.entityName = data.entity_name
                    this.questionData = data.stats
                })
        } else if (this.practitionerId) {
            this.$api.getAggregateReportDataForPractitioner(this.practitionerId)
                .then((data) => {
                    this.entityName = data.entity_name
                    this.questionData = data.stats
                })
        }
    },
})
</script>
